import {
  CloseSquareOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Navigation } from '@constants/Navigation';
import { JobStatusType } from '@openapi';
import api from '@services/api';
import { Job } from '@services/types';
import { downloadFile } from '@services/utils';
import { Button, Card, Modal, Space, Table, TablePaginationConfig, Input } from 'antd';
import Column from 'antd/lib/table/Column';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

const { Search } = Input;

const Jobs = () => {
  const [jobs, setJobs] = React.useState<Job[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [searchResult, setSearchResult] = React.useState('');
  const history = useHistory();
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    total: jobs.length,
  });

  const handleSearch = React.useCallback(
    async (newPagination = pagination) => {
      if (searchResult.length >= 0) {
        try {
          setIsLoading(true);
          const newJobs = await api.jobs.getJobs({
            filter: searchResult,
            limit: newPagination.pageSize,
            offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
          });
          setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
            showSizeChanger: true,
            total: newJobs.totalElementCount || newJobs.elements.length,
          });
          setJobs(newJobs.elements as Job[]);
        } catch (err) {
          console.error('handleSearch ~ err', err);
          toast.error('Jobs was not loaded due to error');
        } finally {
          setIsLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResult],
  );

  const handleTableChange = React.useCallback((newPagination: TablePaginationConfig) => {
    setPagination(newPagination);
    handleSearch(newPagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showDeleteAllConfirm = React.useCallback(() => {
    Modal.confirm({
      title: 'Are you sure that you want to delete all of the jobs?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.jobs.deleteAllJobs();
          handleSearch();
          toast.success('Jobs deleted successfully');
        } catch (error) {
          console.log('Jobs ~ onOk: ~ error', error);
          toast.error('Jobs was not deleted successfully');
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showDeleteConfirm = React.useCallback((job: Job) => {
    Modal.confirm({
      title: 'Are you sure that you want to delete this job?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.jobs.deleteJob(job.id);
          handleSearch();
          toast.success('Job deleted successfully');
        } catch (error) {
          console.log('Job ~ onOk: ~ error', error);
          toast.error('Job was not deleted successfully');
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showCancelConfirm = React.useCallback(
    (job: Job) => {
      Modal.confirm({
        title: 'Are you sure that you want to cancel this job?',
        icon: <ExclamationCircleOutlined />,
        okText: 'Cancel job',
        okType: 'danger',
        onOk: async () => {
          try {
            await api.jobs.cancelJob(job.id);
            toast.success('Job canceled successfully');
          } catch (error) {
            console.log('Job ~ onOk: ~ error', error);
            toast.error('Job wasn`t canceled successfully.');
          } finally {
            handleSearch();
          }
        },
        onCancel() {
          console.log('Cancel');
        },
      });
    },
    [handleSearch],
  );

  React.useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult.length]);

  const outputHandle = async (job: Job) => {
    if (job?.id) {
      const response = await api.jobs.getJobReport(job?.id);
      if (response) {
        const filename = `${job?.type.toLowerCase()}_(${job.id}).csv.gz`;
        if (filename) {
          downloadFile(response, filename);
        }
      }
    }
  };

  return (
    <Card
      title="Job management"
      extra={
        <Space>
          <Search
            onChange={(e) => {
              setSearchResult(e.target.value);
            }}
            placeholder="Search"
            allowClear
            enterButton
            size="middle"
            onSearch={() => handleSearch()}
          />
          <Button type="primary" onClick={() => handleSearch()}>
            Refresh
          </Button>
          <Button type="primary" danger onClick={showDeleteAllConfirm}>
            Delete
          </Button>
        </Space>
      }
      bodyStyle={{ padding: 0 }}
    >
      <Table
        loading={isLoading}
        dataSource={jobs}
        rowKey={(record: Job) => record.id}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1300 }}
      >
        <Column
          title="Created at"
          dataIndex="createdAt"
          key="createdAt"
          render={(date: Job['createdAt']) => date?.toLocaleString()}
        />
        <Column title="Type" dataIndex="type" />
        <Column title="Status" dataIndex="status" />
        <Column title="Job info" dataIndex="info" />
        <Column
          title="Progression"
          dataIndex="id"
          render={(_id, job: Job) =>
            `${Math.round(((job?.processedItemCount || 0) / (job?.totalItemCount || 1)) * 100)}%`
          }
        />
        <Column
          title="Output"
          dataIndex="id"
          render={(_id, job: Job) =>
            job?.reportFileStatus === 'EXISTS' ? (
              <Button disabled={job?.status !== 'FINISHED'} type="primary" onClick={() => outputHandle(job)}>
                <DownloadOutlined />
              </Button>
            ) : (
              'No file'
            )
          }
        />
        <Column title="Error message" dataIndex="errorMessage" />
        <Column
          title="Actions"
          key="action"
          width="100px"
          fixed="right"
          align="center"
          render={(_text, job: Job) => (
            <Space size="middle">
              <InfoCircleOutlined onClick={() => history.push(Navigation.JobInfo.replace(':id', job.id))} />
              {job.status === JobStatusType.Running ? (
                <CloseSquareOutlined onClick={() => showCancelConfirm(job)} />
              ) : (
                <DeleteOutlined onClick={() => showDeleteConfirm(job)} />
              )}
            </Space>
          )}
        />
      </Table>
    </Card>
  );
};

export default React.memo(Jobs);
